import type { CSSProperties } from 'react'
import { palette } from '../components/styles/colors'

export const hexRGBA = (hex: string, opacity = 1) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    if (!result) {
        return null
    }
    const r = parseInt(result[1], 16)
    const g = parseInt(result[2], 16)
    const b = parseInt(result[3], 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

export const RGBtoHSL = (r: number, g: number, b: number) => {
    // Make r, g, and b fractions of 1
    r /= 255
    g /= 255
    b /= 255

    // Find greatest and smallest channel values
    const cmin = Math.min(r, g, b)
    const cmax = Math.max(r, g, b)
    const delta = cmax - cmin
    let h = 0
    let s = 0
    let l = 0

    // Calculate hue
    // No difference
    if (delta === 0) h = 0
    // Red is max
    else if (cmax === r) h = ((g - b) / delta) % 6
    // Green is max
    else if (cmax === g) h = (b - r) / delta + 2
    // Blue is max
    else h = (r - g) / delta + 4

    h = Math.round(h * 60)

    // Make negative hues positive behind 360°
    if (h < 0) h += 360

    // Calculate lightness
    l = (cmax + cmin) / 2

    // Calculate saturation
    s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

    // Multiply l and s by 100
    s = Number((s * 100).toFixed(1))
    l = Number((l * 100).toFixed(1))

    return { h, s, l }
}

export const hexToRGB = (hexColor: string) => {
    if (!hexColor) throw new Error('hexColor is required')
    const color = parseInt(hexColor.replace('#', ''), 16)
    const r = (color >> 16) & 0xff
    const g = (color >> 8) & 0xff
    const b = color & 0xff
    return { r, g, b }
}

export const hexToHSL = (hexColor: string) => {
    const { r, g, b } = hexToRGB(hexColor)
    return RGBtoHSL(r, g, b)
}

export const lighten = (hexColor: string, amount = 88) => {
    if (hexColor === '#000000') {
        return '#FFFFFF'
    }

    if (hexColor[0] === '#') {
        hexColor = hexColor.slice(1)
    }

    const num = parseInt(hexColor, 16)

    let r = (num >> 16) + amount
    if (r > 255) {
        r = 255
    } else if (r < 0) {
        r = 0
    }

    let b = ((num >> 8) & 0x00ff) + amount
    if (b > 255) {
        b = 255
    } else if (b < 0) {
        b = 0
    }

    let g = (num & 0x0000ff) + amount
    if (g > 255) {
        g = 255
    } else if (g < 0) {
        g = 0
    }

    return '#' + (g | (b << 8) | (r << 16)).toString(16)
}

export const lightenNormalized = (hexColor: string, x = 0.34375) => {
    if (hexColor === '#000000') {
        return '#FFFFFF'
    }

    if (hexColor[0] === '#') {
        hexColor = hexColor.slice(1)
    }

    const num = parseInt(hexColor, 16)

    const mF = x < 0 ? 1 - Math.abs(x) : 1 - x
    const aF = x < 0 ? 0 : 255 * x

    let r = (num >> 16) * mF + aF
    if (r > 255) {
        r = 255
    } else if (r < 0) {
        r = 0
    }

    let b = ((num >> 8) & 0x00ff) * mF + aF
    if (b > 255) {
        b = 255
    } else if (b < 0) {
        b = 0
    }

    let g = (num & 0x0000ff) * mF + aF
    if (g > 255) {
        g = 255
    } else if (g < 0) {
        g = 0
    }

    return '#' + (g | (b << 8) | (r << 16)).toString(16)
}

export const getUserColor = (hexColor: string) => {
    if (!hexColor) return '#000000'
    return hexColor
}

export const getModelColor = (hexColor: string, darkMode?: boolean) => {
    if (!hexColor) return !darkMode ? '#000000' : '#ffffff'
    if (!darkMode) return hexColor
    const hsl = hexToHSL(hexColor)
    if (hsl.l > 67) {
        return hexColor
    }
    return lighten(hexColor)
}

export const getPriorMissMFCCrownStyles = (priorRank: number) => {
    return {
        background:
            priorRank === 1
                ? palette.missMFCGold
                : priorRank === 2
                  ? palette.missMFCSilver
                  : priorRank === 3
                    ? palette.missMFCBronze
                    : null,
    }
}

export const getNameStyles = (
    level: number,
    userChatColor: string,
): CSSProperties => {
    const nextStyles: CSSProperties = {
        color: userChatColor,
    }
    switch (level) {
        case 5:
            nextStyles.fontWeight = 700
            break
        case 4:
            nextStyles.fontWeight = 600
            break
        case 2:
            nextStyles.fontWeight = 500
            break
        case 1:
            nextStyles.fontWeight = 400
            break
        default:
            nextStyles.fontWeight = 300
    }
    return nextStyles
}

export function isValidHexColor(color: string) {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color)
}
